import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Plus, Upload, Check, X, Clock, FileText, User, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';

const DOCUMENT_TYPES = ['ID', 'PLAN', 'CONSENT', 'MEDICAL', 'OTHER'];
const STATUS_COLORS = {
  REQUESTED: 'bg-blue-100 text-blue-800',
  UPLOADED: 'bg-yellow-100 text-yellow-800',
  REVIEWED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
};

export default function ParticipantDocumentRequestsPage() {
  const { participantId } = useParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participant, setParticipant] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReview, setShowReview] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/document-requests?participantId=${participantId}&pageSize=100`);
      setRequests(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load document requests');
    } finally {
      setLoading(false);
    }
  }, [participantId]);

  const fetchParticipant = async () => {
    try {
      const res = await api.get(`/participants/${participantId}`);
      setParticipant(res.data.data);
    } catch (err) {}
  };

  useEffect(() => {
    fetchRequests();
    fetchParticipant();
  }, [fetchRequests, participantId]);

  const handleReview = async (reqId) => {
    try {
      await api.post(`/document-requests/${reqId}/review`, { notes: 'Approved' });
      toast.success('Document reviewed and approved');
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to review document');
    }
  };

  const handleReject = async (reqId, reason) => {
    try {
      await api.post(`/document-requests/${reqId}/reject`, { reason });
      toast.success('Document request rejected');
      fetchRequests();
      setShowReview(false);
      setSelectedRequest(null);
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to reject document');
    }
  };

  return (
    <div className="space-y-6" data-testid="participant-doc-requests-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Document Requests</h1>
          <p className="text-muted-foreground">
            {participant ? `${participant.first_name} ${participant.last_name}` : 'Loading...'} - NDIS: {participant?.ndis_number || '...'}
          </p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="create-request-btn"><Plus className="h-4 w-4 mr-2" />Request Document</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Request Document from Portal</DialogTitle></DialogHeader>
            <CreateRequestForm participantId={participantId} onSuccess={() => { setShowCreate(false); fetchRequests(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document Type</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Portal User</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : requests.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No document requests</TableCell></TableRow>
              ) : requests.map(req => (
                <TableRow key={req.id} className="hover:bg-muted/50" data-testid={`request-row-${req.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{req.documentType}</span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">{req.message || '-'}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[req.status] || ''}`}>
                      {req.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {req.portalUserName ? (
                      <div className="text-sm">
                        <p>{req.portalUserName}</p>
                        <p className="text-xs text-muted-foreground">{req.portalUserEmail}</p>
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-sm">{req.createdAt?.slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {req.status === 'UPLOADED' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleReview(req.id)} className="text-green-600" data-testid={`approve-${req.id}`}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedRequest(req); setShowReview(true); }} className="text-red-600" data-testid={`reject-${req.id}`}>
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Document</DialogTitle></DialogHeader>
          {selectedRequest && <RejectForm onReject={(reason) => handleReject(selectedRequest.id, reason)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateRequestForm({ participantId, onSuccess }) {
  const [form, setForm] = useState({ documentType: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.documentType) {
      toast.error('Please select a document type');
      return;
    }
    setLoading(true);
    try {
      await api.post('/document-requests', {
        participantId,
        documentType: form.documentType,
        message: form.message || undefined
      });
      toast.success('Document request created');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to create request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Document Type *</Label>
        <Select value={form.documentType} onValueChange={(v) => setForm(f => ({ ...f, documentType: v }))}>
          <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
          <SelectContent>
            {DOCUMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Message to Portal User</Label>
        <Textarea value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Explain what document is needed..." rows={3} />
      </div>
      <Button type="submit" disabled={loading} className="w-full">{loading ? 'Creating...' : 'Create Request'}</Button>
    </form>
  );
}

function RejectForm({ onReject }) {
  const [reason, setReason] = useState('');
  return (
    <div className="space-y-4">
      <div>
        <Label>Rejection Reason *</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain why the document was rejected..." rows={3} />
      </div>
      <Button onClick={() => onReject(reason)} disabled={!reason} className="w-full" variant="destructive">Reject Document</Button>
    </div>
  );
}
